import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { BrandingContext, type Branding } from './branding-context';

const defaultBranding: Branding = {
  companyName: 'SIDIGO Musikschule',
  companyAddress: '',
  logoUrl: '',
  primaryColor: '#4485df',
  secondaryColor: '#6b7280',
  legalCompany: '',
  legalAddress: '',
  legalEmail: '',
  imprintUrl: '',
  privacyUrl: '',
  termsUrl: '',
  legalText: '',
};

const PUBLIC_BRANDING_DISABLED_KEY = 'sidigo-public-branding-disabled';

const hasAuthToken = (): boolean => {
  if (typeof window === 'undefined') return false;
  return Boolean(localStorage.getItem('sidigo-token') || sessionStorage.getItem('sidigo-token'));
};

const isHttpStatusError = (error: unknown, status: number): boolean => {
  if (!(error instanceof Error)) return false;
  return error.message.includes(`HTTP ${status}`);
};

function hexToHslVar(hex: string): string {
  const clean = hex.replace('#', '');
  const bigint = parseInt(clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean, 16);
  const r = ((bigint >> 16) & 255) / 255;
  const g = ((bigint >> 8) & 255) / 255;
  const b = (bigint & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  const hh = Math.round(h * 360);
  const ss = Math.round(s * 100);
  const ll = Math.round(l * 100);
  return `${hh} ${ss}% ${ll}%`;
}

// Lesbare Schrift auf einer Branding-Farbe: die (dunkle oder helle) Schrift mit dem
// höheren WCAG-Kontrast. Ohne das bliebe die Vordergrundfarbe des Themes stehen – im
// Dark Mode z. B. fast weiße Schrift auf einer hellen Sekundärfarbe.
const DARK_FOREGROUND = { hsl: '222.2 47.4% 11.2%', luminance: 0.0137 };
const LIGHT_FOREGROUND = { hsl: '210 40% 98%', luminance: 0.9546 };

function relativeLuminance(hex: string): number {
  const clean = hex.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map(c => c + c).join('') : clean;
  const channel = (offset: number) => {
    const value = parseInt(full.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

function foregroundFor(hex: string): string {
  const background = relativeLuminance(hex);
  const contrast = (fg: number) => (Math.max(fg, background) + 0.05) / (Math.min(fg, background) + 0.05);
  return contrast(DARK_FOREGROUND.luminance) >= contrast(LIGHT_FOREGROUND.luminance)
    ? DARK_FOREGROUND.hsl
    : LIGHT_FOREGROUND.hsl;
}

function applyBrandingToCssVars(b: Branding) {
  const root = document.documentElement;
  try {
    root.style.setProperty('--primary', hexToHslVar(b.primaryColor));
    root.style.setProperty('--primary-foreground', foregroundFor(b.primaryColor));
    root.style.setProperty('--secondary', hexToHslVar(b.secondaryColor));
    root.style.setProperty('--secondary-foreground', foregroundFor(b.secondaryColor));
    // Nur Ton und Sättigung, nicht die Helligkeit: Wie hell eine Hover-Fläche
    // sein muss, weiss das Theme (hell/dunkel), nicht die Marke. Ohne das blieb
    // `--accent` auf einem festen Grün stehen — sichtbar an jedem Knopf ohne
    // eigene Fläche, egal welche CI-Farbe eingestellt war.
    const [ton, saettigung] = hexToHslVar(b.primaryColor).split(' ');
    root.style.setProperty('--brand-h', ton);
    root.style.setProperty('--brand-s', saettigung);
  } catch {
    return;
  }
}

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBrandingState] = useState<Branding>(defaultBranding);

  useEffect(() => {
    applyBrandingToCssVars(branding);
  }, [branding]);

  const refresh = async () => {
    const authAvailable = hasAuthToken();
    const publicDisabled = typeof window !== 'undefined'
      ? sessionStorage.getItem(PUBLIC_BRANDING_DISABLED_KEY) === '1'
      : false;

    try {
      if (!publicDisabled) {
        const data = await api.get<Branding>('/api/public/branding');
        if (typeof window !== 'undefined') {
          sessionStorage.removeItem(PUBLIC_BRANDING_DISABLED_KEY);
        }
        setBrandingState({ ...defaultBranding, ...data });
        return;
      }
    } catch (error) {
      if (isHttpStatusError(error, 404) && typeof window !== 'undefined') {
        sessionStorage.setItem(PUBLIC_BRANDING_DISABLED_KEY, '1');
      }
    }

    if (!authAvailable) return;

    try {
      // Fallback fuer alte/gesicherte Deployments
      const data = await api.get<Branding>('/api/settings/branding');
      setBrandingState({ ...defaultBranding, ...data });
    } catch {
      // ignore, keep defaults
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const setBranding = (b: Branding) => {
    setBrandingState(b);
  };

  const value = useMemo(() => ({ branding, setBranding, refresh }), [branding]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};



